import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Modal from "./modal";
import Item from "./item";
import { labels, request, time } from "./api";

export default function History({ clientId, board, disabled, onClose, onAction }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let active = true;
    request(`clients/${clientId}/history`)
      .then((data) => {
        if (active) setDetail(data);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [clientId]);
  const more = async (kind) => {
    setLoading(true);
    try {
      const data = await request(
        `clients/${clientId}/history?${kind === "events" ? "before_id" : "before_sighting_id"}=${
          kind === "events" ? detail.next_event : detail.next_sighting
        }`
      );
      setDetail((old) => ({
        ...old,
        [kind]: [...old[kind], ...data[kind]],
        [kind === "events" ? "next_event" : "next_sighting"]:
          kind === "events" ? data.next_event : data.next_sighting
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal title={detail?.client.name || "Client details"} onClose={onClose}>
      <div className="cb-dialog__body ted-form-fields">
        {error && (
          <p role="alert" className="ted-error">
            {error}
          </p>
        )}
        {!detail && !error && <p role="status">Loading care history…</p>}
        {detail && (
          <>
            <p>
              ID {detail.client.id} · {detail.client.residence} ·{" "}
              {detail.client.follow_up === "none"
                ? "No TED follow-up"
                : `${detail.client.follow_up} TED follow-up`}
            </p>
            {(detail.client.deleted || detail.client.deceased) && (
              <p className="ted-error">
                {detail.client.deleted ? "Deleted" : "Deceased"} profile. Care history is retained.
              </p>
            )}
            <h3>Care items</h3>
            {detail.items.length ? (
              detail.items.map((item) => (
                <Item
                  key={item.id}
                  item={item}
                  client={detail.client}
                  board={board}
                  disabled={disabled}
                  onAction={onAction}
                />
              ))
            ) : (
              <p>No care items.</p>
            )}
            <h3>{board.supervisor ? "Audit history" : "Notes and current-shift outcomes"}</h3>
            {!detail.events.length && <p>No recorded events.</p>}
            {detail.events.map((event) => (
              <div className="ted-history-entry" key={event.id}>
                <strong>
                  {labels[event.type] || event.type}
                  {event.item_id && !event.description ? ` · Item #${event.item_id}` : ""}
                </strong>
                <p className="cb-muted">
                  {event.actor} · {time(event.at, board.time_zone, true)}
                  {event.sighting_id ? ` · sighting #${event.sighting_id}` : ""}
                </p>
                {event.description && (
                  <p>
                    {event.description} · revision {event.revision}
                    {event.target_date ? ` · target ${event.target_date}` : ""}
                  </p>
                )}
                {event.note && <p className="ted-note">{event.note}</p>}
              </div>
            ))}
            {detail.next_event && (
              <button className="ccw-button" disabled={loading} onClick={() => more("events")}>
                Load earlier events
              </button>
            )}
            <h3>{board.supervisor ? "Sightings" : "Sightings this shift"}</h3>
            {!detail.sightings.length && <p>No sightings recorded.</p>}
            {detail.sightings.map((sighting) => (
              <div className="ted-history-entry" key={sighting.id}>
                <strong>
                  #{sighting.id} · {time(sighting.seen_at, board.time_zone, true)}
                </strong>
                <p>
                  {sighting.actor} · Seen at {sighting.location} · Residence at the time:{" "}
                  {sighting.residence}
                </p>
                {sighting.surfaced_items.map((surfaced) => (
                  <p key={`${surfaced.id}-${surfaced.revision}`}>
                    {surfaced.description || `Item #${surfaced.id}`} · revision {surfaced.revision}:{" "}
                    {sighting.outcomes
                      .filter(
                        (outcome) =>
                          outcome.item_id === surfaced.id && outcome.revision === surfaced.revision
                      )
                      .map((outcome) => labels[outcome.type])
                      .join(", ") || "No outcome recorded"}
                  </p>
                ))}
                {sighting.outcomes
                  .filter(
                    (outcome) =>
                      !sighting.surfaced_items.some(
                        (entry) =>
                          entry.id === outcome.item_id && entry.revision === outcome.revision
                      )
                  )
                  .map((outcome) => (
                    <p key={outcome.id}>
                      Item #{outcome.item_id} · revision {outcome.revision}: {labels[outcome.type]}{" "}
                      (updated after this sighting)
                    </p>
                  ))}
              </div>
            ))}
            {detail.next_sighting && (
              <button className="ccw-button" disabled={loading} onClick={() => more("sightings")}>
                Load earlier sightings
              </button>
            )}
          </>
        )}
      </div>
      <div className="cb-dialog__footer">
        {detail && board.supervisor && detail.client.follow_up !== "none" && (
          <button
            type="button"
            className="ccw-button"
            disabled={
              disabled ||
              (detail.client.follow_up === "archived" &&
                (detail.client.deceased || detail.client.deleted))
            }
            onClick={() =>
              onAction({
                operation: detail.client.follow_up === "archived" ? "reactivate" : "archive",
                client: detail.client
              })
            }
          >
            {detail.client.follow_up === "archived" ? "Reactivate follow-up" : "Archive follow-up"}
          </button>
        )}
        <button type="button" className="ccw-button ccw-button--quiet" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
History.propTypes = {
  clientId: PropTypes.number.isRequired,
  board: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired
};
